import {
  Card,
  Stack,
  Table,
  Button,
  Container,
  TableBody,
  Typography,
  TableContainer,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import Scrollbar from 'src/components/scrollbar';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { UserType, UserFilters, UserFilterKey, UserFilterValue } from 'src/type/user';
import { useBoolean } from 'src/hooks/use-boolean';
import usePagination from 'src/hooks/use-pagination';
import { fParams, ParamsType } from 'src/utils/format-params';
import { toastError } from 'src/utils/format-error';
import axios, { endpoints } from 'src/utils/axios';
import { Pagination } from 'src/type/utils';
import TableLoading from 'src/components/table/table-loading';
import { emptyRows } from 'src/components/table/utils';
import Iconify from 'src/components/iconify';
import UserTableRow from '../user-table-row';
import UserTableFiltersResult from '../user-table-filters-result';
import UserTableToolbar from '../user-table-toolbar';
import CreateUserDialog from '../create-user-dialog';

const defaultFilters: UserFilters = {
  name: null,
  level: 'all',
  verification: 'all',
};

const tableHead = [
  { id: 'user', label: 'ลูกค้า' },
  { id: 'isEnable', label: 'สถานะ', width: 160 },
  { id: 'isVerifyEmail', label: 'ยืนยันตัวตน', width: 140 },
  { id: 'isPreRegistered', label: 'ลงทะเบียนล่วงหน้า', width: 160 },
  // { id: 'status', label: 'เครดิต', width: 160 },
  { id: 'level', label: 'ระดับ', width: 160 },
  { id: 'action', width: 60 },
];

export default function UserView() {
  const initialize = useBoolean(true);
  const [filters, setFilters] = useState(defaultFilters);
  const createUserDialog = useBoolean();

  const canReset = !!filters.name || filters.level !== 'all' || filters.verification !== 'all';

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<UserType>();

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      level: filters.level === 'all' ? undefined : filters.level,
      verifyStatus: filters.verification === 'all' ? undefined : filters.verification,
      // search: filters.name?.label,
      userId: filters.name?.value,
      page: currentPage,
      limit: rowsPerPage,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    handleGetInviteCode(params);
  }, [currentPage, rowsPerPage, filters.level, filters.verification, filters.name?.value]);

  const handleGetInviteCode = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();
      const { data } = await axios.get<Pagination<UserType>>(endpoints.users.get, {
        params: fParams(params),
      });

      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  const handleFilters = useCallback((name: UserFilterKey, value: UserFilterValue) => {
    onResetPage();
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleResetFilters = useCallback(() => {
    onResetPage();
    setFilters(defaultFilters);
  }, []);

  const handleReload = useCallback(() => {
    initialize.onTrue();
    const params = {
      level: filters.level === 'all' ? undefined : filters.level,
      verifyStatus: filters.verification === 'all' ? undefined : filters.verification,
      userId: filters.name?.value,
      page: currentPage,
      limit: rowsPerPage,
    };
    handleGetInviteCode(params);
  }, [
    currentPage,
    prevPage,
    rowsPerPage,
    filters.level,
    filters.verification,
    filters.name?.value,
    meta.lastDocId,
    meta.firstDocId,
  ]);

  return (
    <Container>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">รายการผู้ใช้งาน</Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={createUserDialog.onTrue}
          >
            เพิ่มผู้ใช้
          </Button>
        </Stack>

        <Card>
          <UserTableToolbar filters={filters} onFilters={handleFilters} disabledSearch={false} />

          {canReset && (
            <UserTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={meta.totalResults}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={rowsPerPage}>
                    {tableData.map((row) => (
                      <UserTableRow key={row.uid} row={row} />
                    ))}

                    <TableEmptyRows
                      show={!initialize.value}
                      height={76}
                      emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                    />

                    <TableNoData notFound={!tableData.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={meta.totalResults}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Card>
      </Stack>

      <CreateUserDialog
        open={createUserDialog.value}
        onClose={createUserDialog.onFalse}
        handleReload={handleReload}
      />
    </Container>
  );
}
